import React, { useContext, useEffect, useState } from 'react';
import { Box, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { walletApi } from '../../../services/api/wallet';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { ff } from '../../../theme';
import { walletColors } from '../walletTheme';
import {
  formatWithdrawalDateLabel,
  formatWithdrawalSubtitle,
  formatWithdrawalTitle,
  type WalletWithdrawalListItem,
} from '../utils/walletWithdrawalsUtils';

const listCardSx = {
  bg: 'white',
  border: '1px solid',
  borderColor: walletColors.cardBorder,
  borderRadius: '20px',
  overflow: 'hidden',
} as const;

export interface WalletWithdrawalsProps {
  enabled?: boolean;
  refreshKey?: number;
  onWithdrawalClick?: (withdrawalId: string) => void;
}

/** Compact bank withdrawal history. */
const WalletWithdrawals: React.FC<WalletWithdrawalsProps> = ({
  enabled = false,
  refreshKey = 0,
  onWithdrawalClick,
}) => {
  const { influencerData } = useContext(InfluencerDataContext);
  const [withdrawals, setWithdrawals] = useState<WalletWithdrawalListItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !influencerData?.id) {
      setWithdrawals([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    walletApi
      .getWithdrawals({ influencerId: influencerData.id })
      .then((res) => {
        if (!cancelled) {
          setWithdrawals(res.withdrawals);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWithdrawals([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, influencerData?.id, refreshKey]);

  if (!enabled) {
    return null;
  }

  const rowsAreInteractive = Boolean(onWithdrawalClick);

  return (
    <Box id="wallet-withdrawals" fontFamily="display">
      <Text textStyle="display" fontSize="20px" color={walletColors.textPrimary} mb={3} px={1}>
        Withdrawals
      </Text>

      <Box {...listCardSx}>
        {loading ? (
          <Flex align="center" justify="center" gap={2} px={4} py={6}>
            <Spinner size="sm" color={walletColors.textSecondary} />
            <Text fontSize="14px" color={walletColors.textSecondary}>
              Loading withdrawals…
            </Text>
          </Flex>
        ) : withdrawals.length === 0 ? (
          <Box px={4} py={6}>
            <Text fontSize="14px" color={walletColors.textSecondary}>
              Withdrawals to your bank will show up here.
            </Text>
          </Box>
        ) : (
          <VStack spacing={0} align="stretch">
            {withdrawals.map((withdrawal, index) => (
              <Flex
                key={withdrawal.id}
                align="center"
                gap={3}
                px="16px"
                py="12px"
                minH="64px"
                w="100%"
                borderTop={index > 0 ? '1px solid' : undefined}
                borderColor={walletColors.cardBorder}
                cursor={rowsAreInteractive ? 'pointer' : 'default'}
                transition="background-color 0.15s ease"
                _hover={rowsAreInteractive ? { bg: ff.grey } : undefined}
                _active={rowsAreInteractive ? { bg: ff.line } : undefined}
                role={rowsAreInteractive ? 'button' : undefined}
                tabIndex={rowsAreInteractive ? 0 : undefined}
                onClick={
                  rowsAreInteractive ? () => onWithdrawalClick?.(withdrawal.id) : undefined
                }
                onKeyDown={
                  rowsAreInteractive
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onWithdrawalClick?.(withdrawal.id);
                        }
                      }
                    : undefined
                }
              >
                <Box flex={1} minW={0}>
                  <Text fontSize="15px" fontWeight="600" color={walletColors.textPrimary} noOfLines={1}>
                    {formatWithdrawalTitle(withdrawal.destinationLabel)}
                  </Text>
                  <Text fontSize="13px" color={walletColors.textSecondary} noOfLines={1}>
                    {formatWithdrawalSubtitle(withdrawal.status)}
                  </Text>
                </Box>
                <Box textAlign="right" flexShrink={0}>
                  <Text textStyle="display" fontSize="17px" color={walletColors.textPrimary}>
                    -${withdrawal.amount.toFixed(withdrawal.amount % 1 === 0 ? 0 : 2)}
                  </Text>
                  <Text fontSize="11px" color={walletColors.textMuted}>
                    {formatWithdrawalDateLabel(withdrawal)}
                  </Text>
                </Box>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default WalletWithdrawals;
